import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import type { ExternalUser, ExternalUserApp } from '@/types/externalUser';
import type { Endpoint } from '@/types/endpoint';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Pencil } from 'lucide-react';

const APP_STATUSES = ['pending', 'running', 'stopped', 'failed'] as const;

export function ExternalUserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<ExternalUser | null>(null);
  const [apps, setApps] = useState<ExternalUserApp[]>([]);
  const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [appsLoading, setAppsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState('');
  const [endpointFilter, setEndpointFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [editingName, setEditingName] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    const fetchUser = async () => {
      if (!userId) return;
      try {
        setLoading(true);
        setError(null);
        const result = await api.externalUsers.get(parseInt(userId));
        if (controller.signal.aborted) return;
        setUser(result);
        setEditValue(result.display_name || '');
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to load user');
        if (err instanceof Error && err.message.includes('not found')) {
          toast.error('User not found');
          navigate('/external-users');
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    const fetchEndpoints = async () => {
      try {
        const result = await api.endpoints.listAll();
        if (controller.signal.aborted) return;
        setEndpoints(result);
      } catch {
        // Silent fail for endpoints
      }
    };

    fetchUser();
    fetchEndpoints();

    return () => controller.abort();
  }, [userId, navigate]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchApps = async () => {
      if (!userId) return;
      try {
        setAppsLoading(true);
        const result = await api.externalUsers.getApps(parseInt(userId), {
          status: statusFilter || undefined,
          endpoint_id: endpointFilter ? parseInt(endpointFilter) : undefined,
          search: searchQuery || undefined,
          page,
          limit: 20,
        });
        if (controller.signal.aborted) return;
        setApps(result.data);
        setTotal(result.total);
        setTotalPages(result.total_pages);
      } catch (err) {
        if (controller.signal.aborted) return;
        toast.error(err instanceof Error ? err.message : 'Failed to load apps');
      } finally {
        if (!controller.signal.aborted) {
          setAppsLoading(false);
        }
      }
    };

    if (user) {
      fetchApps();
    }

    return () => controller.abort();
  }, [user, statusFilter, endpointFilter, searchQuery, page, userId, refreshKey]);

  const handleRefresh = () => setRefreshKey(k => k + 1);

  const handleEditName = () => {
    setEditingName(true);
    setEditValue(user?.display_name || '');
  };

  const handleCancelEdit = () => {
    setEditingName(false);
    setEditValue(user?.display_name || '');
  };

  const handleSaveName = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      const updated = await api.externalUsers.update(parseInt(userId), { display_name: editValue });
      setUser(updated);
      setEditingName(false);
      toast.success('Display name updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status: ExternalUserApp['status']) => {
    const colors: Record<ExternalUserApp['status'], string> = {
      pending: 'bg-yellow-500',
      running: 'bg-green-500',
      stopped: 'bg-gray-500',
      failed: 'bg-red-500',
    };
    return (
      <Badge className={colors[status]}>
        {status.toUpperCase()}
      </Badge>
    );
  };

  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/external-users')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Users
        </Button>
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            Loading user...
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/external-users')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Users
        </Button>
        <Card>
          <CardContent className="p-8 text-center text-destructive">
            {error || 'User not found'}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate('/external-users')}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Users
      </Button>

      {/* User Info Card */}
      <Card>
        <CardHeader>
          <CardTitle>External User Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">ID</label>
              <p className="text-lg">{user.id}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">External User ID</label>
              <p className="text-lg font-mono">{user.external_user_id}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Display Name</label>
              {editingName ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="w-48"
                    autoFocus
                  />
                  <Button
                    size="sm"
                    disabled={saving}
                    onClick={handleSaveName}
                  >
                    {saving && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-lg">{user.display_name || '-'}</p>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleEditName}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Apps Count</label>
              <p className="text-lg">
                <Badge variant="secondary">{user.apps_count}</Badge>
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Created At</label>
              <p className="text-lg">{formatRelativeTime(user.created_at)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Apps Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Apps owned by this user</CardTitle>
              <CardDescription>
                {total} apps total
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Search by app name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-48"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-md border border-input bg-background px-3 py-1 text-sm"
              >
                <option value="">All Statuses</option>
                {APP_STATUSES.map(s => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
              <select
                value={endpointFilter}
                onChange={(e) => setEndpointFilter(e.target.value)}
                className="rounded-md border border-input bg-background px-3 py-1 text-sm"
              >
                <option value="">All Endpoints</option>
                {endpoints.map(ep => (
                  <option key={ep.id} value={ep.id}>{ep.name}</option>
                ))}
              </select>
              <Button size="sm" variant="outline" onClick={handleRefresh}>
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {appsLoading && apps.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              Loading apps...
            </div>
          ) : apps.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              This user has no apps deployed yet.
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>App Catalog</TableHead>
                    <TableHead>Endpoint</TableHead>
                    <TableHead>Stack</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apps.map((app) => (
                    <TableRow
                      key={app.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/apps/${app.id}`)}
                    >
                      <TableCell className="font-medium">
                        <Link to={`/apps/${app.id}`} onClick={(e) => e.stopPropagation()}>
                          {app.name}
                        </Link>
                      </TableCell>
                      <TableCell>{getStatusBadge(app.status)}</TableCell>
                      <TableCell>{app.app_catalog?.name || '-'}</TableCell>
                      <TableCell>{app.endpoint?.name || '-'}</TableCell>
                      <TableCell>{app.stack?.name || '-'}</TableCell>
                      <TableCell className="text-sm">
                        {formatRelativeTime(app.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Previous
                  </Button>
                  <span className="text-sm">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page === totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}